/**
 * Copyright 2025 MadeByTatwa LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Smart fix handler - detects and applies fixes for common project issues
 */
import type { Fix, RunPlan } from '../types/index.js';
import { fileExists, readPackageJson } from '../utils/fs.js';
import { isPortInUse, findFreePort } from '../utils/port.js';
import { getInstallCommand } from '../utils/pm.js';
import { logger } from '../utils/logger.js';
import { join } from 'path';

/**
 * Detect all fixable issues in a project
 */
export const detectFixes = async (plan: RunPlan): Promise<Fix[]> => {
  const fixes: Fix[] = [];

  // Check for missing dependencies
  const dependencyFix = await detectDependencyIssues(plan);
  if (dependencyFix) {
    fixes.push(dependencyFix);
  }

  // Check for port conflicts
  const portFix = await detectPortConflicts(plan);
  if (portFix) {
    fixes.push(portFix);
  }

  // Check for missing environment files
  const envFix = detectEnvIssues(plan);
  if (envFix) {
    fixes.push(envFix);
  }

  // Check for config issues
  const configFix = await detectConfigIssues(plan);
  if (configFix) {
    fixes.push(configFix);
  }

  return fixes;
};

/**
 * Detect missing dependencies
 */
const detectDependencyIssues = async (plan: RunPlan): Promise<Fix | null> => {
  const hasNodeModules = fileExists(join(plan.cwd, 'node_modules'));
  const hasPackageJson = fileExists(join(plan.cwd, 'package.json'));

  if (!hasNodeModules && hasPackageJson && plan.pm) {
    const installCmd = getInstallCommand(plan.pm);
    return {
      kind: 'install',
      details: 'Dependencies not installed',
      command: installCmd,
    };
  }

  // Check if package.json has dependencies but node_modules is outdated
  if (hasPackageJson && hasNodeModules) {
    const pkg = await readPackageJson(plan.cwd);
    if (pkg) {
      const hasDeps = Object.keys((pkg.dependencies as Record<string, string>) || {}).length > 0;
      const hasDevDeps = Object.keys((pkg.devDependencies as Record<string, string>) || {}).length > 0;
      
      if ((hasDeps || hasDevDeps) && plan.pm) {
        const packageLockPath = join(plan.cwd, 'package-lock.json');
        const nodeModulesPath = join(plan.cwd, 'node_modules');
        
        // Check if package-lock.json is newer than node_modules
        try {
          const fs = await import('fs/promises');
          const lockStats = await fs.stat(packageLockPath).catch(() => null);
          const nmStats = await fs.stat(nodeModulesPath).catch(() => null);
          
          if (lockStats && nmStats && lockStats.mtime > nmStats.mtime) {
            return {
              kind: 'install',
              details: 'Dependencies outdated (package-lock.json is newer)',
              command: getInstallCommand(plan.pm),
            };
          }
        } catch {
          // Ignore stat errors
        }
      }
    }
  }

  return null;
};

/**
 * Detect port conflicts
 */
const detectPortConflicts = async (plan: RunPlan): Promise<Fix | null> => {
  // Extract port from environment variables or common defaults
  const envPort = plan.env?.PORT ? parseInt(plan.env.PORT, 10) : null;
  
  // Common framework default ports
  const defaultPorts: Record<string, number> = {
    'Next.js': 3000,
    'Vite': 5173,
    'Create React App': 3000,
    'Angular': 4200,
    'Express API': 3000,
    'Fastify API': 3000,
    'NestJS API': 3000,
  };

  const portToCheck = envPort || (plan.detectedType ? defaultPorts[plan.detectedType] : null);

  if (portToCheck) {
    const inUse = await isPortInUse(portToCheck);
    if (inUse) {
      const freePort = await findFreePort(portToCheck + 1);
      return {
        kind: 'port',
        details: `Port ${portToCheck} is already in use`,
        command: `PORT=${freePort}`,
      };
    }
  }

  return null;
};

/**
 * Detect missing environment files
 */
const detectEnvIssues = (plan: RunPlan): Fix | null => {
  const hasEnvExample = fileExists(join(plan.cwd, '.env.example'));
  const hasEnv = fileExists(join(plan.cwd, '.env'));

  if (hasEnvExample && !hasEnv) {
    return {
      kind: 'env',
      details: 'Missing .env file (found .env.example)',
      command: 'cp .env.example .env',
    };
  }

  return null;
};

/**
 * Detect config file issues
 */
const detectConfigIssues = async (plan: RunPlan): Promise<Fix | null> => {
  // Check for TypeScript projects without tsconfig.json
  const hasPackageJson = fileExists(join(plan.cwd, 'package.json'));
  const hasTsConfig = fileExists(join(plan.cwd, 'tsconfig.json'));

  if (hasPackageJson && !hasTsConfig) {
    const pkg = await readPackageJson(plan.cwd);
    if (pkg) {
      const deps = { ...(pkg.dependencies as Record<string, string>), ...(pkg.devDependencies as Record<string, string>) };
      if (deps.typescript) {
        return {
          kind: 'config',
          details: 'TypeScript installed but missing tsconfig.json',
          command: 'npx tsc --init',
        };
      }
    }
  }

  return null;
};

/**
 * Apply a fix
 */
export const applyFix = async (fix: Fix, cwd: string, verbose = false): Promise<boolean> => {
  logger.info(`Applying fix: ${fix.details}`);

  if (!fix.command) {
    logger.warn('No command specified for fix');
    return false;
  }

  try {
    if (fix.kind === 'port') {
      // Port fixes are applied as environment variables, not executed
      logger.success(`Suggested: ${fix.command}`);
      return true;
    }

    // Execute the fix command
    const { execa } = await import('execa');
    
    logger.debug(`Running: ${fix.command}`, verbose);
    
    await execa(fix.command, {
      cwd,
      stdio: 'inherit',
      shell: true,
    });

    logger.success('Fix applied successfully');
    return true;
  } catch (error) {
    logger.error(`Failed to apply fix: ${error instanceof Error ? error.message : String(error)}`);
    return false;
  }
};

/**
 * Get a user-friendly description of a fix
 */
export const getFixDescription = (fix: Fix): string => {
  switch (fix.kind) {
    case 'install':
      return `📦 Install dependencies: ${fix.command}`;
    case 'port':
      return `🔌 Use different port: ${fix.command}`;
    case 'env':
      return `🔐 Create environment file: ${fix.command}`;
    case 'config':
      return `⚙️  Generate config file: ${fix.command}`;
    default:
      return `🔧 ${fix.details}`;
  }
};

